import papaparse from 'papaparse';
import type { Row } from 'write-excel-file/browser';
import { downloadBlobFile } from '../../../common/utils';

export const base64ToJson = (base64String: string) => {
  const json = Buffer.from(base64String, "base64").toString();
  return JSON.parse(json);
}

// DEPRECATED
export const convertToCSV_ = (objArray: string) => {
  const array = typeof objArray != 'object' ? JSON.parse(objArray) : objArray;
  let str = '';

  for (let i = 0; i < array.length; i++) {
      let line = '';
      for (const index in array[i]) {
          if (line != '') line += ','

          line += array[i][index];
      }

      str += line + '\r\n';
  }

  return str;
}

export const convertToCSV = (objArray: Record<string, unknown>[], includeHeaders = true) => {
  return papaparse.unparse(objArray, { header: includeHeaders });
}

export const exportCSVFile = (
  headers: Record<string, string>,
  items: Record<string, unknown>[],
  fileTitle?: string,
) => {
  if (headers) {
    items.unshift(headers);
  }

  const csv = convertToCSV(items, false);
  const exportedFilenmae = fileTitle + '.csv' || 'export.csv';

  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
  if ((navigator as any).msSaveBlob) { // IE 10+
      (navigator as any).msSaveBlob(blob, exportedFilenmae);
  } else {
    const link = document.createElement("a");
      if (link.download !== undefined) { // feature detection
          // Browsers that support HTML5 download attribute
          const url = URL.createObjectURL(blob);
          link.setAttribute("href", url);
          link.setAttribute("download", exportedFilenmae);
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
      }
  }
}

export const exportXLSXFile = async (
  headers: Record<string, string>,
  items: Record<string, unknown>[],
  fileTitle?: string,
) => {
  // Lazy-loaded: write-excel-file is not tiny, and most consumers of this
  // module never trigger an XLSX export, so we don't want it in their bundle.
  const writeXlsxFile = (await import('write-excel-file/browser')).default;

  const keys = headers ? Object.keys(headers) : Object.keys(items[0] || {});
  // Rows are built by column key, not by the header label, so two columns
  // sharing the same display header text can't collide.
  const dataRows: Row[] = items.map((item) => keys.map((key) => item[key] as Row[number]));
  // Matches exportCSVFile's own `if (headers) items.unshift(headers)`: no
  // headers, no header row, on both formats alike.
  const sheetData: Row[] = headers
    ? [keys.map((key) => headers[key] ?? key), ...dataRows]
    : dataRows;

  // toBlob() already sets the correct spreadsheet mime type, and routing
  // through downloadBlobFile (not a plain <a download>) makes it also work
  // inside the native mobile app's Capacitor share handler.
  const blob = await writeXlsxFile(sheetData).toBlob();
  await downloadBlobFile(blob, `${fileTitle || 'export'}.xlsx`);
}

// Matches unambiguous numeric text only: either plain digits with no
// separators ("1234"), or Spanish-grouped digits (".", every group exactly 3
// digits, e.g. "1.234.567") with an optional "," decimal part, or a bare
// comma-decimal with no grouping ("45,7"). A lone "." followed by anything
// other than a full 3-digit group is left alone on purpose — that shape is
// just as likely to be an en-US decimal ("1234.56") or a date ("08.09.2026")
// as a Spanish thousands separator, and guessing wrong silently corrupts the
// value (e.g. "1234.56" -> 123456).
const SPANISH_NUMBER_PATTERN = /^-?\d{1,3}(\.\d{3})+(,\d+)?$|^-?\d+(,\d+)?$/;

// Digit runs that must stay text even though they match SPANISH_NUMBER_PATTERN:
// zero-padded codes ("007", "00123" — order/lot/machine/SKU identifiers are
// routinely zero-padded, and coercion silently drops the padding) and
// anything long enough to lose precision as an IEEE-754 double (barcodes,
// long internal IDs).
const IDENTIFIER_PATTERN = /^-?0\d|^-?\d{16,}$/;

// Coerces Spanish-locale numeric-looking text ("1.234,56", "45,7 %") to a
// real number so Excel treats it as one (SUM/sort/pivot work, no
// "number stored as text" marker). Anything that doesn't match the pattern
// above — including plain en-US decimals, dates, zero-padded codes, and
// long identifier-shaped digit runs — is left as a string.
export const toNumericCell = (value: string | number): string | number => {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  const core = trimmed.endsWith('%') ? trimmed.slice(0, -1).trim() : trimmed;

  if (!SPANISH_NUMBER_PATTERN.test(core) || IDENTIFIER_PATTERN.test(core)) return value;

  const normalized = core.replace(/\./g, '').replace(',', '.');
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) return value;

  // Integer-shaped values must survive the round-trip exactly. This rejects
  // "0.500" (Spanish grouping never yields a leading "0" group, so it's
  // actually an en-US decimal) and grouped digit runs long enough to lose
  // IEEE-754 precision — both slip past IDENTIFIER_PATTERN, which only
  // inspects unseparated digits. Decimals are exempt: "45,70" -> 45.7 is a
  // formatting change, not data loss, so it's fine that it doesn't round-trip
  // as a string.
  if (!core.includes(',') && String(parsed) !== normalized) return value;

  return parsed;
};

// Builds a custom number format that appends a literal "%" after the
// digits — as opposed to Excel's built-in percentage format (a bare,
// unescaped "%" in the format code), which multiplies the stored value by
// 100 for display. toNumericCell stores "45,7 %" as the plain number 45.7
// (not 0.457), so the built-in format would misdisplay it as "4570.0%" —
// this one doesn't scale. The decimal-place count is read from the original
// text rather than hardcoded, so "45,75 %" keeps both decimals ("45.75%")
// instead of always rounding to one ("45.8%") — but capped: Excel rejects
// custom number formats with more than 30 decimal places (the workbook then
// opens with a repair prompt, failing the whole download, not just this
// cell), and past ~15 the extra digits are just IEEE-754 noise anyway since
// toNumericCell's result is a double.
export const buildPercentCellFormat = (rawValue: string): string => {
  const core = rawValue.trim().replace(/%$/, '').trim();
  const decimalPlaces = Math.min(core.match(/,(\d+)$/)?.[1].length ?? 0, 15);
  const decimalPattern = decimalPlaces > 0 ? `.${'0'.repeat(decimalPlaces)}` : '';
  return `0${decimalPattern}"%"`;
};

/**
 * Positional variant for callers that already have plain rows (e.g. cells
 * read straight from a rendered <table>). Unlike exportXLSXFile, this never
 * keys a row by its header text, so two columns sharing the same header
 * label can't collide and silently drop data.
 *
 * The first row is assumed to be the header and is left untouched; every
 * other row has its cells coerced to numbers where they look numeric. Cells
 * that were percent text keep a "%" number format so the column still reads
 * as a percentage in Excel, not a bare number.
 */
export const exportXLSXFileFromRows = async (
  rows: (string | number)[][],
  fileTitle?: string,
) => {
  const writeXlsxFile = (await import('write-excel-file/browser')).default;

  const sheetData: Row[] = rows.map((row, rowIndex) => {
    if (rowIndex === 0) return row;
    return row.map((cell) => {
      const converted = toNumericCell(cell);
      if (
        typeof converted === 'number' &&
        typeof cell === 'string' &&
        cell.trim().endsWith('%')
      ) {
        return { value: converted, format: buildPercentCellFormat(cell) };
      }
      return converted;
    });
  });

  const blob = await writeXlsxFile(sheetData).toBlob();
  await downloadBlobFile(blob, `${fileTitle || 'export'}.xlsx`);
}

// Shared by the inline markdown table download menu (chat and report text blocks):
// reads a rendered <table> into plain rows from the DOM.
export const getTableRows = (tableEl: HTMLTableElement): string[][] => {
  const rows: string[][] = [];
  tableEl.querySelectorAll('tr').forEach((tr) => {
    const cells: string[] = [];
    tr.querySelectorAll('th, td').forEach((cell) => {
      cells.push(cell.textContent?.trim() || '');
    });
    if (cells.length) rows.push(cells);
  });
  return rows;
};

export const rowsToCsv = (rows: string[][]): string =>
  rows
    .map((r) => r.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(','))
    .join('\n');

export const getExportFilename = () => {
  const n = new Date();
  const pad = (v: number) => String(v).padStart(2, '0');
  return `FactoryGPT - ${
    pad(n.getDate())}-${
      pad(n.getMonth() + 1)}-${n.getFullYear()} ${
        pad(n.getHours())}.${
          pad(n.getMinutes())}.${
            pad(n.getSeconds())}`;
};
